import React, { useState, useEffect } from 'react';
import { UserPlus, Shield, Trash2, Edit2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const EmpleadosTab = () => {
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    telefono: '',
    rol: 'empleado',
    permisos: {
      puede_aprobar_veterinarios: false,
      puede_aprobar_posts: false,
      puede_subir_pdfs: false,
      puede_gestionar_empleados: false
    }
  });
  const { toast } = useToast();

  useEffect(() => {
    loadEmpleados();
  }, []);

  const loadEmpleados = async () => {
    try {
      setLoading(true);
      const response = await api.get('/empleados/');
      setEmpleados(response.data);
    } catch (error) {
      console.error('Error loading empleados:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los empleados',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await api.put(`/empleados/${editingId}`, {
          nombre: formData.nombre,
          telefono: formData.telefono,
          permisos: formData.permisos
        });
        toast({
          title: '✅ Empleado actualizado',
          description: 'La información ha sido actualizada',
        });
      } else {
        await api.post('/empleados/', formData);
        toast({
          title: '✅ Empleado creado',
          description: 'El empleado ha sido creado exitosamente',
        });
      }
      
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadEmpleados();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo guardar el empleado',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (empleado) => {
    setFormData({
      nombre: empleado.nombre,
      email: empleado.email,
      password: '',
      telefono: empleado.telefono || '',
      rol: empleado.rol || 'empleado',
      permisos: empleado.permisos
    });
    setEditingId(empleado.id);
    setShowForm(true);
  };

  const handleDelete = async (empleadoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este empleado?')) {
      return;
    }
    
    try {
      await api.delete(`/empleados/${empleadoId}`);
      toast({
        title: 'Empleado eliminado',
        description: 'El empleado ha sido eliminado exitosamente',
      });
      loadEmpleados();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el empleado',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      telefono: '',
      rol: 'empleado',
      permisos: {
        puede_aprobar_veterinarios: false,
        puede_aprobar_posts: false,
        puede_subir_pdfs: false,
        puede_gestionar_empleados: false
      }
    });
  };

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-[#1e3a8a]">👥 Gestión de Empleados</CardTitle>
          <p className="text-sm text-gray-600">Total: {empleados.length}</p>
        </div>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-[#84cc16] hover:bg-[#65a30d]"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Nuevo Empleado
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {showForm && (
          <Card className="mb-6 border-2 border-[#84cc16]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {editingId ? 'Editar Empleado' : 'Nuevo Empleado'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    resetForm();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Nombre completo *</label>
                    <Input
                      value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      disabled={editingId}
                      required={!editingId}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Contraseña {editingId && '(dejar vacío para no cambiar)'}
                    </label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required={!editingId}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Teléfono</label>
                    <Input
                      value={formData.telefono}
                      onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Rol *</label>
                    <select
                      value={formData.rol}
                      onChange={(e) => setFormData({...formData, rol: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="empleado">Empleado Regular</option>
                      <option value="contador">Contador (Facturación)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.rol === 'contador' ? 'Acceso completo a gestión de facturas' : 'Permisos personalizables'}
                    </p>
                  </div>
                </div>

                {formData.rol === 'empleado' && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Permisos (solo para Empleados Regulares)
                    </h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permisos.puede_aprobar_veterinarios}
                        onChange={(e) => setFormData({
                          ...formData,
                          permisos: {...formData.permisos, puede_aprobar_veterinarios: e.target.checked}
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Puede aprobar veterinarios</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permisos.puede_aprobar_posts}
                        onChange={(e) => setFormData({
                          ...formData,
                          permisos: {...formData.permisos, puede_aprobar_posts: e.target.checked}
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Puede aprobar posts del blog</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permisos.puede_subir_pdfs}
                        onChange={(e) => setFormData({
                          ...formData,
                          permisos: {...formData.permisos, puede_subir_pdfs: e.target.checked}
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Puede subir PDFs de resultados</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permisos.puede_gestionar_empleados}
                        onChange={(e) => setFormData({
                          ...formData,
                          permisos: {...formData.permisos, puede_gestionar_empleados: e.target.checked}
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Puede gestionar empleados</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-[#1e40af] hover:bg-[#1e3a8a]">
                    {editingId ? 'Actualizar' : 'Crear'} Empleado
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                      resetForm();
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {empleados.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <UserPlus className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p>No hay empleados registrados</p>
          </div>
        ) : (
          <div className="space-y-3">
            {empleados.map((empleado) => (
              <Card key={empleado.id} className="border-2">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg text-[#1e3a8a]">{empleado.nombre}</h3>
                        {!empleado.is_active && (
                          <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                            Inactivo
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Email:</span>
                          <span className="ml-2 font-medium">{empleado.email}</span>
                        </div>
                        {empleado.telefono && (
                          <div>
                            <span className="text-gray-500">Teléfono:</span>
                            <span className="ml-2 font-medium">{empleado.telefono}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {empleado.permisos?.puede_aprobar_veterinarios && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            ✓ Aprobar veterinarios
                          </span>
                        )}
                        {empleado.permisos?.puede_aprobar_posts && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                            ✓ Aprobar posts
                          </span>
                        )}
                        {empleado.permisos?.puede_subir_pdfs && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                            ✓ Subir PDFs
                          </span>
                        )}
                        {empleado.permisos?.puede_gestionar_empleados && (
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                            ✓ Gestionar empleados
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(empleado)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(empleado.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmpleadosTab;
